// components/MandateTable.js

import { useState, Fragment } from "react";

export default function MandateTable({ mandatesData, idToken }) {
  const mandates = mandatesData?.items || [];
  const [downloading, setDownloading] = useState({});
  const [expandedRows, setExpandedRows] = useState({}); // Track expanded rows by ID
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" }); // Track sort column and direction
  const [searchQuery, setSearchQuery] = useState(""); // Track search input

  const handleDownload = async (s3Location, mandateName) => {
    try {
      setDownloading((prev) => ({ ...prev, [mandateName]: true }));
      const response = await fetch(
        "https://fhu83jnvn8.execute-api.eu-central-1.amazonaws.com/PROD/getS3Object",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ s3Location: s3Location.trim() }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const { url } = data;

      const link = document.createElement("a");
      link.href = url;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download file");
    } finally {
      setDownloading((prev) => ({ ...prev, [mandateName]: false }));
    }
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Filter and sort mandates
  const filteredAndSortedMandates = [...mandates]
    .filter((mandate) => {
      const query = searchQuery.toLowerCase();
      return Object.values(mandate).some((value) =>
        value &&
        typeof value === "string" &&
        value.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue == null || bValue == null) return 0;
      if (typeof aValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      return sortConfig.direction === "asc"
        ? aValue - bValue
        : bValue - aValue;
    });

  return (
    <div className="px-4 sm:px-6 lg:px-8 mt-10">
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full p-2 border rounded dark:bg-gray-800 dark:border-gray-700 dark:text-white"
        />
      </div>
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-left sm:px-6 lg:px-8">
            <table className="relative min-w-full divide-y divide-gray-300 dark:divide-white/15">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-0 dark:text-white cursor-pointer"
                    onClick={() => handleSort("name")}
                  >
                    Name {sortConfig.key === "name" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer"
                    onClick={() => handleSort("ip")}
                  >
                    IP {sortConfig.key === "ip" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer"
                    onClick={() => handleSort("timestamp")}
                  >
                    Timestamp {sortConfig.key === "timestamp" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    scope="col"
                    className="py-3.5 pr-4 pl-3 sm:pr-0"
                  >
                    <span className="sr-only">Download</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                {filteredAndSortedMandates.map((mandate) => (
                  <Fragment key={mandate.id}>
                    <tr
                      onClick={() => toggleRowExpansion(mandate.id)}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <td className="py-4 pr-3 pl-4 text-sm font-medium whitespace-nowrap text-gray-900 sm:pl-0 dark:text-white">
                        {mandate.name}
                      </td>
                      <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {mandate.ip || "N/A"}
                      </td>
                      <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {mandate.timestamp
                          ? new Date(mandate.timestamp).toLocaleString()
                          : "N/A"}
                      </td>
                      <td className="py-4 pr-4 pl-3 text-right text-sm font-medium whitespace-nowrap sm:pr-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row toggle
                            handleDownload(mandate.s3Location, mandate.name);
                          }}
                          disabled={downloading[mandate.name] || !mandate.s3Location}
                          className={`text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 pr-2 ${
                            downloading[mandate.name] || !mandate.s3Location
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                        >
                          {downloading[mandate.name] ? "Downloading..." : "Download"}
                          <span className="sr-only">, {mandate.name}</span>
                        </button>
                      </td>
                    </tr>
                    {expandedRows[mandate.id] && (
                      <tr>
                        <td colSpan="4" className="bg-gray-50 dark:bg-gray-900 p-4">
                          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-300">
                            <div>
                              <strong>ID:</strong> {mandate.id || "N/A"}
                            </div>
                            <div>
                              <strong>Address:</strong> {mandate.address || "N/A"}
                            </div>
                            <div>
                              <strong>Ort:</strong> {mandate.ort || "N/A"}
                            </div>
                            <div>
                              <strong>PLZ:</strong> {mandate.plz || "N/A"}
                            </div>
                            <div>
                              <strong>S3 Location:</strong> {mandate.s3Location || "N/A"}
                            </div>
                            {mandate.firmenname && (
                              <div>
                                <strong>Firmenname:</strong> {mandate.firmenname}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}